import { useEffect, useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ConnectorStatusCard } from './ConnectorStatusCard'
import type { ConnectorMetrics } from '../types/topology'

interface Props {
  connectors: ConnectorMetrics[]
  onClose: () => void
}

type Filter = 'all' | 'attention' | 'unknown' | 'running'

/**
 * Same three buckets the summary card counts by, kept in one place so the
 * filter chips and the "n of m" header can never drift from it.
 *
 * "unknown" means Connect did not answer for that connector — it is not a
 * verdict on the connector itself, so it stays out of the attention bucket.
 */
function bucketOf(c: ConnectorMetrics): Exclude<Filter, 'all'> {
  if (c.state === 'UNKNOWN') return 'unknown'
  if (c.state === 'RUNNING' && c.runningTasks >= c.totalTasks) return 'running'
  return 'attention'
}

/** Problems first, then unreachable, then healthy; alphabetical within a bucket. */
const RANK: Record<Exclude<Filter, 'all'>, number> = { attention: 0, unknown: 1, running: 2 }

export function ConnectorListModal({ connectors, onClose }: Props) {
  const [filter, setFilter] = useState<Filter>('all')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const { sorted, counts } = useMemo(() => {
    const counts = { all: connectors.length, attention: 0, unknown: 0, running: 0 }
    for (const c of connectors) counts[bucketOf(c)]++
    const sorted = [...connectors].sort((a, b) => {
      const d = RANK[bucketOf(a)] - RANK[bucketOf(b)]
      return d !== 0 ? d : a.name.localeCompare(b.name)
    })
    return { sorted, counts }
  }, [connectors])

  const visible = filter === 'all' ? sorted : sorted.filter(c => bucketOf(c) === filter)

  const chips: { key: Filter; label: string; tone: string }[] = [
    { key: 'all',       label: 'All',             tone: 'bg-gray-100 text-gray-700' },
    { key: 'attention', label: 'Needs attention', tone: 'bg-red-100 text-red-700' },
    { key: 'unknown',   label: 'Unknown',         tone: 'bg-amber-100 text-amber-700' },
    { key: 'running',   label: 'Running',         tone: 'bg-green-100 text-green-700' },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <div>
            <h3 className="font-semibold text-gray-900">Connectors</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {counts.running} running · {counts.attention} need attention · {counts.unknown} unknown
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 px-4 py-3 border-b shrink-0">
          {chips.map(chip => {
            const n = counts[chip.key]
            return (
              <button
                key={chip.key}
                onClick={() => setFilter(chip.key)}
                disabled={n === 0 && chip.key !== 'all'}
                className={cn(
                  'text-xs px-2.5 py-1 rounded-full font-medium transition-colors',
                  filter === chip.key ? chip.tone : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50',
                  n === 0 && chip.key !== 'all' && 'opacity-40 cursor-not-allowed hover:bg-white'
                )}
              >
                {chip.label} ({n})
              </button>
            )
          })}
        </div>

        {/* List */}
        <div className="overflow-y-auto p-4">
          {counts.unknown > 0 && (filter === 'all' || filter === 'unknown') && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
              {counts.unknown === counts.all
                ? 'Kafka Connect did not answer the status call, so no connector state could be read.'
                : `${counts.unknown} connector${counts.unknown !== 1 ? 's' : ''} returned no status.`}{' '}
              These may well be running — check <code className="font-mono">PIPELINE_KAFKA_CONNECT_URL</code> and
              Connect REST reachability before treating them as outages.
            </p>
          )}
          {visible.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No connectors in this state.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map(c => <ConnectorStatusCard key={c.name} metrics={c} compact />)}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
